import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, ChevronLeft, Send, Anchor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';
import { t } from '@/i18n/translations';
import LanguageSelector from '@/components/LanguageSelector';

const LOGO_HEADER = "https://customer-assets.emergentagent.com/job_yacht-reserve-pro/artifacts/t54ah8ex_logo%203%20BELLA%20BARCA.png";
const LOGO_FOOTER = "https://customer-assets.emergentagent.com/job_yacht-reserve-pro/artifacts/k3tg8tl5_2.png";

const HERO_BG = "https://static.wixstatic.com/media/ce6ce7_6ac85e6129c34274b70a22563156040df000.jpg/v1/fill/w_1920,h_600,fp_0.43_0.40,q_85/ce6ce7_6ac85e6129c34274b70a22563156040df000.jpg";

export default function ContactPage() {
  const { lang } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error();
      toast.success('Message envoyé avec succès ! Nous vous répondrons rapidement.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      toast.error('Erreur lors de l\'envoi. Réessayez ou appelez-nous.');
    }
    setSending(false);
  };

  const contactInfo = [
    {
      icon: Phone,
      title: t('contact.phoneLabel', lang),
      content: '06 87 99 96 39',
      href: 'tel:+33687999639',
      subtitle: 'Appel ou SMS'
    },
    {
      icon: Mail,
      title: t('contact.emailLabel', lang),
      content: 'contact@abellabarca.com',
      href: 'mailto:contact@abellabarca.com',
      subtitle: lang === 'fr' ? 'Réponse sous 24h' : lang === 'en' ? 'Reply within 24h' : lang === 'it' ? 'Risposta entro 24h' : 'Antwort innerhalb 24h'
    },
    {
      icon: MapPin,
      title: t('contact.addressLabel', lang),
      content: "Port de Sant'Ambroggio",
      href: 'https://maps.google.com/?q=Port+de+Sant+Ambroggio+20260+Lumio+Corse',
      subtitle: '20260 Lumio, Corse'
    },
    {
      icon: Clock,
      title: t('contact.hoursLabel', lang),
      content: '9h - 21h',
      href: null,
      subtitle: lang === 'fr' ? 'Lundi au Dimanche' : lang === 'en' ? 'Monday to Sunday' : lang === 'it' ? 'Da Lunedì a Domenica' : 'Montag bis Sonntag'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#dec5ae]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <img src={LOGO_HEADER} alt="A Bella Barca" className="h-14" />
            </Link>
            <Link to="/" className="flex items-center gap-2 text-[#0a57a4] hover:text-[#099dbb] transition-colors text-sm">
              <ChevronLeft className="w-4 h-4" />
              {t('booking.backHome', lang)}
            </Link>
            <LanguageSelector />
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-[#0a57a4]/70" />
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative text-center text-white z-10 px-4"
        >
          <Anchor className="w-10 h-10 mx-auto mb-4 text-[#099dbb]" />
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mb-4">{t('contact.title', lang)}</h1>
          <p className="text-white/80 text-lg max-w-xl mx-auto">
            {t('contact.subtitle', lang)}
          </p>
        </motion.div>
      </section>

      {/* Contact Cards */}
      <section className="py-16 -mt-16 relative z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {contactInfo.map((info, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
              >
                {info.href ? (
                  <a
                    href={info.href}
                    target={info.href.startsWith('http') ? '_blank' : undefined}
                    rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="block bg-white p-6 rounded-sm border border-[#dec5ae]/30 hover:border-[#099dbb] hover:shadow-lg transition-all text-center group h-full"
                    data-testid={`contact-card-${info.title.toLowerCase()}`}
                  >
                    <div className="w-14 h-14 mx-auto mb-4 bg-[#099dbb]/10 rounded-full flex items-center justify-center group-hover:bg-[#099dbb]/20 transition-colors">
                      <info.icon className="w-6 h-6 text-[#099dbb]" />
                    </div>
                    <h3 className="font-heading text-lg text-[#0a57a4] mb-1">{info.title}</h3>
                    <p className="font-semibold text-[#0a57a4] text-sm mb-1">{info.content}</p>
                    <p className="text-xs text-[#858275]">{info.subtitle}</p>
                  </a>
                ) : (
                  <div
                    className="bg-white p-6 rounded-sm border border-[#dec5ae]/30 text-center h-full"
                    data-testid={`contact-card-${info.title.toLowerCase()}`}
                  >
                    <div className="w-14 h-14 mx-auto mb-4 bg-[#099dbb]/10 rounded-full flex items-center justify-center">
                      <info.icon className="w-6 h-6 text-[#099dbb]" />
                    </div>
                    <h3 className="font-heading text-lg text-[#0a57a4] mb-1">{info.title}</h3>
                    <p className="font-semibold text-[#0a57a4] text-sm mb-1">{info.content}</p>
                    <p className="text-xs text-[#858275]">{info.subtitle}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content: Form + Map */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl text-[#0a57a4] mb-2">Envoyez-nous un message</h2>
              <p className="text-[#858275] mb-8">Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais.</p>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-[#0a57a4] font-medium text-sm">Nom complet *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Votre nom"
                      className="mt-1 border-[#dec5ae]/40 focus:border-[#099dbb]"
                      data-testid="contact-name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-[#0a57a4] font-medium text-sm">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="votre@email.com"
                      className="mt-1 border-[#dec5ae]/40 focus:border-[#099dbb]"
                      data-testid="contact-email"
                      required
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone" className="text-[#0a57a4] font-medium text-sm">Téléphone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="06 XX XX XX XX"
                      className="mt-1 border-[#dec5ae]/40 focus:border-[#099dbb]"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject" className="text-[#0a57a4] font-medium text-sm">Sujet</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Objet de votre message"
                      className="mt-1 border-[#dec5ae]/40 focus:border-[#099dbb]"
                      data-testid="contact-subject"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#0a57a4] font-medium text-sm">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Votre message..."
                    rows={6}
                    className="mt-1 border-[#dec5ae]/40 focus:border-[#099dbb] resize-none"
                    data-testid="contact-message"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-[#099dbb] hover:bg-[#0a57a4] text-white py-6 text-base"
                  data-testid="contact-submit-btn"
                >
                  {sending ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Envoi en cours...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      Envoyer le message
                    </span>
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Map + Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="font-heading text-3xl text-[#0a57a4] mb-2">Nous trouver</h2>
              <p className="text-[#858275] mb-4">Retrouvez-nous au Port de Sant'Ambroggio, au coeur de la Balagne.</p>

              {/* Google Maps Embed */}
              <div className="rounded-sm overflow-hidden border border-[#dec5ae]/30 shadow-sm">
                <iframe
                  title="A Bella Barca - Port de Sant'Ambroggio"
                  src="https://maps.google.com/maps?q=A+Bella+Barca+Lumio+Corse&t=&z=16&ie=UTF8&iwloc=B&output=embed"
                  width="100%"
                  height="350"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contact-map"
                />
              </div>

              {/* Additional info card */}
              <div className="bg-[#0a57a4] text-white p-8 rounded-sm">
                <h3 className="font-heading text-xl mb-6">Informations pratiques</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <Clock className="w-5 h-5 text-[#099dbb] mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold mb-1">Horaires d'ouverture</p>
                      <p className="text-white/70 text-sm">Lundi au Dimanche : 9h - 21h</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 text-[#099dbb] mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold mb-1">Accès</p>
                      <p className="text-white/70 text-sm">Port de Sant'Ambroggio, 20260 Lumio, Corse</p>
                      <p className="text-white/70 text-sm">Parking gratuit à proximité</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Anchor className="w-5 h-5 text-[#099dbb] mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold mb-1">Point de départ</p>
                      <p className="text-white/70 text-sm">Présentez-vous 10 minutes avant l'heure de départ au ponton d'embarquement sous le phare vert.</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a57a4] text-white py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
            <img src={LOGO_FOOTER} alt="A Bella Barca" className="h-12" />
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
              <a href="tel:+33687999639" className="text-white/70 hover:text-[#099dbb] transition-colors text-sm flex items-center gap-2">
                <Phone className="w-4 h-4" />
                06 87 99 96 39
              </a>
              <a href="mailto:contact@abellabarca.com" className="text-white/70 hover:text-[#099dbb] transition-colors text-sm flex items-center gap-2">
                <Mail className="w-4 h-4" />
                contact@abellabarca.com
              </a>
            </div>
            <div className="text-white/50 text-xs">
              © 2026 A BELLA BARCA | Conception © 2025 <a href="https://www.webisula.com/" target="_blank" rel="noopener noreferrer" className="text-[#099dbb] hover:text-white transition-colors">WEBISULA</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
